import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { Tv, Bell, Mail, Crown, Globe, Mic, MapPin, Coins, MessageSquare, Star, Wand2, BadgeCheck, LogIn, LogOut, UserCircle2, Radio, Headphones, Sparkles } from "lucide-react";
import PricingModal from "./PricingModal";
import AiSearchBar from "./AiSearchBar";
import AuthModal from "./AuthModal";
import UniversalScorerWidget from "./UniversalScorerWidget";
import { useAuth } from "../auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Primary section nav — exact priority order per editorial directive (2026-05-29):
// Top → Breaking → Local → Politics → Consumer & Economy → Public Safety →
// Tech & AI → Business → Health → Investigations → Sports → Lifestyle → Entertainment
const SECTIONS = [
  { key: "top", label: "Top Stories", path: "/" },
  { key: "breaking", label: "Breaking", path: "/news" },
  { key: "local", label: "Local", path: "/section/local" },
  { key: "world", label: "World", path: "/section/world" },
  { key: "government", label: "Politics", path: "/section/government" },
  { key: "money", label: "Consumer & Economy", path: "/section/money" },
  { key: "safety", label: "Public Safety", path: "/section/local" },
  { key: "technology", label: "Tech & AI", path: "/section/technology" },
  { key: "markets", label: "Business & Markets", path: "/section/markets" },
  { key: "health", label: "Health", path: "/section/health" },
  { key: "podcasts", label: "Investigations", path: "/section/podcasts" },
  { key: "newsstand", label: "Newsstand", path: "/newsstand" },
  { key: "sports", label: "Sports", path: "/section/sports" },
  { key: "lifestyle", label: "Lifestyle", path: "/section/culture" },
  { key: "entertainment", label: "Entertainment", path: "/section/entertainment" },
  { key: "videos", label: "Video", path: "/section/videos" },
  { key: "science", label: "Science", path: "/section/science" },
  { key: "space", label: "Space", path: "/section/space" },
  { key: "ai", label: "AI", path: "/section/ai" },
  { key: "voices", label: "Voices", path: "/section/voices" },
  { key: "editorial", label: "Editorial", path: "/section/editorial" },
  { key: "foreign", label: "Foreign Stations", path: "/section/foreign" },
  { key: "buildeze", label: "Buildeze", path: "/#buildeze" },
];

// Functionality bar (the "second header" — user actions, not categories)
const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "pt", label: "Português" },
  { code: "it", label: "Italiano" },
  { code: "ja", label: "日本語" },
  { code: "ko", label: "한국어" },
  { code: "zh", label: "中文" },
  { code: "ar", label: "العربية" },
  { code: "he", label: "עברית" },
  { code: "ru", label: "Русский" },
  { code: "hi", label: "हिन्दी" },
];

const LogoMark = () => (
  <svg viewBox="0 0 64 64" fill="none" className="w-[26px] h-[26px]">
    <path d="M12 34c14-18 25-18 40 0" stroke="white" strokeWidth="7" strokeLinecap="round" />
    <path d="M16 45h32" stroke="white" strokeWidth="7" strokeLinecap="round" />
    <circle cx="32" cy="24" r="6" fill="white" />
  </svg>
);

export default function Header({ ticker = [], onOpenBrief }) {
  const loc = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, logout } = useAuth();
  const [showPricing, setShowPricing] = useState(false);
  const [authMode, setAuthMode] = useState(null); // null | "login" | "register"
  const [showMenu, setShowMenu] = useState(false);
  const [lang, setLang] = useState(localStorage.getItem("newseze_lang") || "en");
  const [availableCats, setAvailableCats] = useState(null); // null = unfiltered until fetched
  const today = new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });

  useEffect(() => {
    localStorage.setItem("newseze_lang", lang);
    document.documentElement.setAttribute("lang", lang);
  }, [lang]);

  // Allow ?auth=login (e.g. redirect from reset-password page) to auto-open the modal
  useEffect(() => {
    const a = searchParams.get("auth");
    if (a === "login" || a === "register") {
      setAuthMode(a);
      const next = new URLSearchParams(searchParams);
      next.delete("auth");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Hide nav items that have no content. Always keep Headlines and Buildeze.
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/news/sections-available`, { timeout: 6000 })
      .then((r) => { if (alive) setAvailableCats(new Set(r.data.available || [])); })
      .catch(() => { if (alive) setAvailableCats(new Set()); });
    return () => { alive = false; };
  }, []);

  const visibleSections = SECTIONS.filter((s) => {
    if (s.key === "top" || s.key === "buildeze") return true;
    if (availableCats === null) return true; // before fetch, show all
    return availableCats.has(s.key);
  });

  const activeKey = (() => {
    if (loc.pathname === "/") return "top";
    const m = loc.pathname.match(/section\/(\w+)/);
    return m ? m[1] : "";
  })();

  return (
    <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-[var(--line)]">
      {/* utility bar */}
      <div className="utility-bar">
        <div className="max-w-[1280px] mx-auto px-5 py-1.5 flex items-center justify-between text-[11px] tracking-[0.1em] uppercase font-semibold">
          <span data-testid="dateline">{today}</span>
          <div className="hidden md:flex items-center gap-5">
            <span className="flex items-center gap-2"><span className="live-dot" /> Trust-Ranked Wire · refreshed hourly</span>
            <Link to="/admin" className="text-white/70 hover:text-white" data-testid="admin-link">Operator</Link>
          </div>
        </div>
      </div>

      {/* brand row */}
      <div className="max-w-[1280px] mx-auto px-5 h-[78px] flex items-center justify-between gap-5">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="brand-link">
          <span className="logo"><LogoMark /></span>
          <div className="flex flex-col leading-tight">
            <span className="text-[28px] font-black tracking-[-0.04em] text-[var(--ink)]">Newseze</span>
            <span className="text-[9px] tracking-[0.22em] uppercase text-[var(--gold)] font-black">News with Rewards · Earn while you read</span>
          </div>
        </Link>
        <div className="hidden md:flex flex-1 justify-center max-w-[640px]">
          <AiSearchBar />
        </div>
        <div className="hidden md:flex items-center gap-2 shrink-0">
          <button type="button" className="btn" onClick={onOpenBrief} data-testid="header-brief-cta">
            <Mail size={14} /> Daily Brief
          </button>
          <button type="button" className="btn gold" onClick={() => setShowPricing(true)} data-testid="header-upgrade-cta">
            <Crown size={14} /> Go Pro
          </button>
          {user ? (
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowMenu((s) => !s)}
                className="btn"
                aria-label="Account menu"
                data-testid="header-account"
              >
                <UserCircle2 size={14} />
                <span className="max-w-[110px] truncate">{user.name || user.email.split("@")[0]}</span>
              </button>
              {showMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
                  <div className="absolute right-0 mt-1 z-50 bg-white border border-[var(--line)] rounded-lg shadow-xl w-[220px] py-1.5 text-[13px]" data-testid="account-menu">
                    <div className="px-3 py-2 border-b border-[var(--line)]">
                      <div className="font-bold truncate">{user.name || "Newseze reader"}</div>
                      <div className="text-[11px] text-[var(--muted)] truncate">{user.email}</div>
                      <div className="text-[10px] uppercase tracking-[0.15em] text-[var(--gold)] font-black mt-1">{user.tier || "free"} plan</div>
                    </div>
                    <Link to="/rewards" className="block px-3 py-2 hover:bg-[var(--sky)]" onClick={() => setShowMenu(false)} data-testid="account-rewards">My Rewards</Link>
                    {user.role === "admin" && (
                      <Link to="/admin" className="block px-3 py-2 hover:bg-[var(--sky)]" onClick={() => setShowMenu(false)} data-testid="account-admin">Operator panel</Link>
                    )}
                    <button
                      onClick={() => { logout(); setShowMenu(false); }}
                      className="w-full text-left px-3 py-2 hover:bg-[var(--sky)] flex items-center gap-2 text-[var(--red)]"
                      data-testid="account-logout"
                    >
                      <LogOut size={13} /> Sign out
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <button
              type="button"
              className="btn"
              onClick={() => setAuthMode("login")}
              data-testid="header-signin"
            >
              <LogIn size={14} /> Sign in
            </button>
          )}
          <div className="flex items-center gap-1 px-2 py-1.5 rounded-md border border-[var(--line)] bg-white" data-testid="header-lang">
            <Globe size={12} className="text-[var(--muted)]" />
            <select
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              className="text-[12px] font-bold bg-transparent border-0 outline-none cursor-pointer"
              aria-label="Language"
              data-testid="lang-select"
            >
              {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
          </div>
          <button className="btn icon" aria-label="Alerts"><Bell size={14} /></button>
        </div>
      </div>

      {/* mobile AI search */}
      <div className="md:hidden px-3 pb-2">
        <AiSearchBar />
      </div>

      {showPricing && <PricingModal onClose={() => setShowPricing(false)} onNeedSubscribe={onOpenBrief} />}
      <AuthModal open={!!authMode} initialMode={authMode || "login"} onClose={() => setAuthMode(null)} />

      {/* primary section nav (categories) */}
      <div className="section-nav">
        <div className="max-w-[1280px] mx-auto px-2 overflow-x-auto flex items-center whitespace-nowrap">
          {visibleSections.map((s) => (
            <Link
              key={s.key}
              to={s.path}
              className={`${activeKey === s.key ? "active" : ""} ${s.key === "buildeze" ? "text-[var(--gold)] font-black" : ""} shrink-0`}
              data-testid={`nav-${s.key}`}
            >
              {s.label}
            </Link>
          ))}
        </div>
      </div>

      {/* secondary functionality bar (actions) */}
      <div className="bg-[var(--sky)] border-b border-[var(--sky-2)]" data-testid="func-bar">
        <div className="max-w-[1280px] mx-auto px-3 overflow-x-auto flex items-center whitespace-nowrap gap-1 py-1.5">
          <Link to="/rewards" className="func-link bg-[var(--gold)] !text-[#1d1303] font-black rounded-md px-2.5" data-testid="func-rewards">
            <Coins size={12} /> Rewards · Earn $$$
          </Link>
          <UniversalScorerWidget />
          <Link to="/newsroom" className="func-link" data-testid="func-newsroom">
            <Mic size={12} /> Newsroom
          </Link>
          <Link to="/trust" className="func-link" data-testid="func-trust">
            <BadgeCheck size={12} /> Verified Sources
          </Link>
          <Link to="/networks" className="func-link" data-testid="func-networks">
            <Globe size={12} /> Networks
          </Link>
          <Link to="/other-voices" className="func-link" data-testid="func-other-voices">
            <Radio size={12} /> Other Voices
          </Link>
          <Link to="/connect" className="func-link" data-testid="func-connect">
            <Headphones size={12} /> Newseze Connect
          </Link>
          <Link to="/reader-tools" className="func-link" data-testid="func-reader-tools">
            <Sparkles size={12} /> Reader Tools
          </Link>
          <Link to="/lens" className="func-link" data-testid="func-lens">
            <Sparkles size={12} /> Newseze Lens
          </Link>
          <Link to="/cyber" className="func-link" data-testid="func-cyber">
            <Sparkles size={12} /> Cyber Center
          </Link>
          <Link to="/columns" className="func-link" data-testid="func-columns">
            <Sparkles size={12} /> Columns
          </Link>
          <Link to="/extension" className="func-link" data-testid="func-extension">
            <Star size={12} /> Earn Anywhere
          </Link>
          <Link to="/sellpages" className="func-link" data-testid="func-sellpages">
            <Crown size={12} /> Sellpages
          </Link>
          <Link to="/students" className="func-link" data-testid="func-students">
            <Star size={12} /> Students · Free Pro
          </Link>
          <Link to="/benefits" className="func-link" data-testid="func-benefits">
            <Coins size={12} /> Member Benefits
          </Link>
          <Link to="/archives" className="func-link" data-testid="func-archives">
            <Star size={12} /> Archives
          </Link>
          <Link to="/section/markets" className="func-link" data-testid="func-watchlist">
            <Wand2 size={12} /> Watchlist
          </Link>
          <Link to="/section/podcasts" className="func-link" data-testid="func-investigations">
            <Mic size={12} /> Investigations
          </Link>
          <Link to="/#local" className="func-link" data-testid="func-local">
            <MapPin size={12} /> Local
          </Link>
          <Link to="/section/videos" className="func-link" data-testid="func-watch">
            <Tv size={12} /> Watch
          </Link>
          <Link to="/contact" className="func-link" data-testid="func-contact">
            <MessageSquare size={12} /> Contact
          </Link>
          <button onClick={onOpenBrief} className="func-link" data-testid="func-brief">
            <Mail size={12} /> Daily Brief
          </button>
        </div>
      </div>

      {/* breaking / ticker */}
      {ticker?.length > 0 && (
        <div className="bg-white border-b border-[var(--line)] overflow-hidden">
          <div className="max-w-[1280px] mx-auto px-5 py-2 flex items-center gap-4">
            <span className="text-[11px] uppercase tracking-[0.18em] font-black text-[var(--red)] flex-shrink-0 flex items-center gap-2">
              <span className="live-dot red" /> Latest
            </span>
            <div className="overflow-hidden flex-1">
              <div className="marquee-track text-[13px] font-semibold text-[var(--ink)]">
                {[...ticker, ...ticker].map((t, i) => (
                  <Link key={i} to={`/article/${t.id}`} className="flex items-center gap-3 hover:text-[var(--blue)] mr-8">
                    <span className="text-[var(--gold)] uppercase text-[10px] font-black">Newseze · {t.category}</span>
                    <span>{t.title}</span>
                    {t.source && (
                      <>
                        <span className="text-[var(--muted)]">·</span>
                        <span className="text-[var(--muted)] text-[10px] uppercase tracking-[0.12em] font-bold opacity-70">Wire: {t.source}</span>
                      </>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
